import React, { useEffect } from 'react';
import type { FormInstance } from 'antd';
import { Form, Input, Row, Col } from 'antd';
import { useAuth } from '@/contexts/AuthContext';
import { usersService } from '@/services/users.service';
import styles from '../../pages/UpdatePersonalProfilePage.module.css';

interface UpdateBankInfoSectionProps {
  /**
   * userId của nhân sự cần hiển thị thông tin ngân hàng.
   * - Nếu truyền vào: ưu tiên dùng userId này (dùng cho màn Quản lý nhân sự chỉnh hồ sơ staff).
   * - Nếu không truyền: fallback về user hiện tại từ AuthContext (dùng cho trang hồ sơ cá nhân tự cập nhật).
   */
  userId?: number;
  form: FormInstance;
}

export const UpdateBankInfoSection: React.FC<UpdateBankInfoSectionProps> = ({ userId, form }) => {
  const { user } = useAuth();

  // Fetch thông tin ngân hàng từ API
  useEffect(() => {
    const fetchBankInfo = async () => {
      // Ưu tiên userId truyền từ props (màn quản lý nhân sự),
      // nếu không có thì fallback sang user hiện tại (trang hồ sơ cá nhân).
      let targetUserId: number | null = null;

      if (typeof userId === 'number') {
        targetUserId = userId;
      } else if (user?.id) {
        targetUserId = parseInt(user.id.toString(), 10);
      }

      if (!targetUserId || Number.isNaN(targetUserId)) return;

      try {
        const userDetail = await usersService.getUserById(targetUserId);

        // Set giá trị form với thông tin ngân hàng
        form.setFieldsValue({
          bankAccountNumber: userDetail?.bankAccountNumber || '',
          bankAccountName: userDetail?.bankAccountName || '',
          bankName: userDetail?.bankName || '',
          bankBranch: userDetail?.bankBranch || '',
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching bank info in UpdateBankInfoSection:', error);
      }
    };

    fetchBankInfo();
  }, [form, userId, user?.id]);

  return (
    <div className={styles.section}>
      <Row gutter={24} className={styles.formRow}>
        <Col span={8}>
          <Form.Item
            name="bankAccountNumber"
            label="Số tài khoản"
            rules={[{ required: true, message: 'Vui lòng nhập số tài khoản' }]}
          >
            <Input
              placeholder="Số tài khoản"
              size="large"
              className={styles.input}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="bankAccountName"
            label="Tên tài khoản"
            rules={[{ required: true, message: 'Vui lòng nhập tên tài khoản' }]}
          >
            <Input
              placeholder="Tên tài khoản"
              size="large"
              className={styles.input}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="bankName"
            label="Tên ngân hàng"
            rules={[{ required: true, message: 'Vui lòng nhập tên ngân hàng' }]}
          >
            <Input
              placeholder="Tên ngân hàng"
              size="large"
              className={styles.input}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24} className={styles.formRow}>
        <Col span={24}>
          <Form.Item
            name="bankBranch"
            label="Chi nhánh"
          >
            <Input
              placeholder="Chi nhánh"
              size="large"
              className={styles.input}
            />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};

